import { useEffect, useState } from "react";
import type { RecipientStats } from "@feedback-hub/contract/browser";

import { ApiError, getTopRecipients, lookUpRecipient } from "../../api.js";
import { formatDate } from "../../format.js";
import type { SortDir, SortValue } from "../../sort.js";
import { useDebouncedValue } from "../../useDebouncedValue.js";
import { ClearableInput } from "../ClearableInput.js";
import { count } from "./mailFormat.js";
import { SortableTh } from "./SortableTh.js";
import { useSort } from "./useSort.js";

type Key = "email" | "sends" | "deliveries" | "bounces" | "complaints" | "firstSeenAt" | "lastSentAt";

const DIRS: Record<Key, SortDir> = {
  email: "asc",
  sends: "desc",
  deliveries: "desc",
  bounces: "desc",
  complaints: "desc",
  firstSeenAt: "desc",
  lastSentAt: "desc",
};

function value(r: RecipientStats, key: Key): SortValue {
  return r[key];
}

export function RecipientsTab() {
  const [topRecipients, setTopRecipients] = useState<RecipientStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const [lookup, setLookup] = useState<RecipientStats | null | "none">("none");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const { sorted, sort, onSort } = useSort<RecipientStats, Key>(
    topRecipients,
    value,
    { key: "sends", dir: "desc" },
    DIRS,
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getTopRecipients(50);
        if (!cancelled) setTopRecipients(res.recipients);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load mail stats.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!debouncedSearch) {
      setLookup("none");
      setLookupError(null);
      setLookupBusy(false);
      return;
    }
    let cancelled = false;
    setLookupBusy(true);
    setLookupError(null);
    void (async () => {
      try {
        const res = await lookUpRecipient(debouncedSearch);
        if (!cancelled) setLookup(res.recipient);
      } catch (err) {
        if (!cancelled) {
          setLookupError(err instanceof ApiError ? err.message : "Lookup failed.");
          setLookup("none");
        }
      } finally {
        if (!cancelled) setLookupBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const th = (label: string, k: Key) => (
    <SortableTh label={label} sortKey={k} activeKey={sort.key} dir={sort.dir} onSort={onSort} />
  );

  return (
    <div>
      <div className="panel section">
        <h3>Recipient lookup</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Have we mailed this address, how often, and did they complain?
        </p>
        <div className="filters" style={{ marginBottom: "0.75rem" }}>
          <div className="field" style={{ minWidth: "18rem" }}>
            <label htmlFor="mail-recipient-search">Email address</label>
            <ClearableInput
              id="mail-recipient-search"
              type="search"
              placeholder="name@example.com"
              autoComplete="off"
              value={search}
              onChange={setSearch}
            />
          </div>
        </div>
        {lookupBusy && <p className="muted">Looking up…</p>}
        {lookupError && <p className="error">{lookupError}</p>}
        {!lookupBusy && !lookupError && debouncedSearch && lookup === null && (
          <p className="muted">No record of mailing {debouncedSearch}.</p>
        )}
        {!lookupBusy && !lookupError && lookup && lookup !== "none" && (
          <div className="table-wrap">
            <table className="queue">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Bounced</th>
                  <th>Complained</th>
                  <th>First seen</th>
                  <th>Last sent</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ cursor: "default" }}>
                  <td className="mono">{lookup.email}</td>
                  <td>{count(lookup.sends)}</td>
                  <td>{count(lookup.deliveries)}</td>
                  <td>{count(lookup.bounces)}</td>
                  <td>
                    {count(lookup.complaints)}
                    {lookup.complaints > 0 && (
                      <span className="warn-mark" title="Has complained">
                        complaint
                      </span>
                    )}
                  </td>
                  <td>{lookup.firstSeenAt ? formatDate(lookup.firstSeenAt) : "—"}</td>
                  <td>{lookup.lastSentAt ? formatDate(lookup.lastSentAt) : "—"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel section" style={{ marginTop: "1rem" }}>
        <h3>Top recipients by send count</h3>
        {loading && <p className="muted">Loading mail stats…</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !error && sorted.length === 0 && <p className="muted">No sends recorded yet.</p>}
        {!loading && !error && sorted.length > 0 && (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Sorting reorders the top 50 by send count.
            </p>
            <div className="table-wrap">
              <table className="queue">
                <thead>
                  <tr>
                    {th("Address", "email")}
                    {th("Sent", "sends")}
                    {th("Delivered", "deliveries")}
                    {th("Bounced", "bounces")}
                    {th("Complained", "complaints")}
                    {th("First seen", "firstSeenAt")}
                    {th("Last sent", "lastSentAt")}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr key={r.email} style={{ cursor: "default" }}>
                      <td className="mono">{r.email}</td>
                      <td>{count(r.sends)}</td>
                      <td>{count(r.deliveries)}</td>
                      <td>{count(r.bounces)}</td>
                      <td>{count(r.complaints)}</td>
                      <td>{r.firstSeenAt ? formatDate(r.firstSeenAt) : "—"}</td>
                      <td>{r.lastSentAt ? formatDate(r.lastSentAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
